import { useState, useEffect, useCallback, useMemo } from 'react';
import * as y from 'yup';
import ServiceCRUDChatTS from '../services/ServiceCRUDCHATTS';

/**
 * @typedef {Object} Option
 * @property {string|number} value
 * @property {string} label
 */

const AgentQueueRefSchema = y.object({
  queueId: y.number().required(),
  queueName: y.string().required()
});

const AgentSchema = y.object({
  login: y.string().required(),
  name: y.string().defined(),
  queues: y.array().of(AgentQueueRefSchema).required()
});

const AgentInQueueDetailSchema = y.object({
  login: y.string().required(),
  name: y.string().defined(),
  dateIn: y.number().required()
});

const QueueDetailSchema = y.object({
  queueId: y.number().required(),
  queueName: y.string().required(),
  agents: y.array().of(AgentInQueueDetailSchema).required()
});

export const AgentsLoggedInQueuesSchema = y.object({
  agents: y.array().of(AgentSchema).required(),
  queues: y.array().of(QueueDetailSchema).required()
});

/**
 * Hook para gerenciar a relação entre Agentes Logados e Filas.
 * Utiliza safeValidateSync para garantir a integridade dos dados recebidos.
 *
 * @param {Object} [props]
 * @param {boolean} [props.manual=false] - Se true, bloqueia o fetch automático no mount.
 */
const useAgentsLoggedInQueues = ({ manual = false } = {}) => {
  const [state, setState] = useState({
    /** @type {y.InferType<typeof AgentsLoggedInQueuesSchema> | null} */
    response: null,
    isLoading: false,
    isError: false,
    error: null
  });

  const service = useMemo(
    () => new ServiceCRUDChatTS('/flex-chat-integration/api'),
    []
  );

  const allQueueOptions = useMemo(
    () =>
      state.response?.queues?.map(({ queueId, queueName }) => ({
        label: queueName,
        value: queueId
      })) ?? [],
    [state.response]
  );

  const allAgentOptions = useMemo(
    () =>
      state.response?.agents?.map(({ name, login }) => ({
        label: name,
        value: login
      })) ?? [],
    [state.response]
  );

  /**
   * Mapa de filas indexado por ID.
   * Estrutura: { [queueId]: { ...queueData, agentOptions: Option[] } }
   */
  const queuesMap = useMemo(() => {
    if (!state.response?.queues) return {};
    return state.response.queues.reduce((acc, curr) => {
      acc[curr.queueId] = {
        ...curr,
        agentOptions: curr.agents.map((a) => ({
          label: a.name,
          value: a.login
        }))
      };
      return acc;
    }, {});
  }, [state.response]);

  /**
   * Mapa de agentes indexado por Login.
   * Estrutura: { [login]: { ...agentData, queueOptions: Option[] } }
   */
  const agentsMap = useMemo(() => {
    if (!state.response?.agents) return {};
    return state.response.agents.reduce((acc, curr) => {
      acc[curr.login] = {
        ...curr,
        queueOptions: curr.queues.map((q) => ({
          label: q.queueName,
          value: q.queueId
        }))
      };
      return acc;
    }, {});
  }, [state.response]);

  const fetch = useCallback(async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
      isError: false,
      error: null
    }));

    try {
      const response = await service.getAgentsLoggedInQueues();
      const rawData = response?.data?.data ?? { agents: [], queues: [] };

      const result = AgentsLoggedInQueuesSchema.safeValidateSync(rawData);

      if (result.success) {
        setState((prevState) => ({
          ...prevState,
          response: result.data,
          isError: false,
          error: null
        }));
      } else {
        // Erro de validação estrutural do schema
        setState((prevState) => ({
          ...prevState,
          response: null,
          isError: true,
          error: result.error
        }));
      }
    } catch (err) {
      // Erro de rede ou exceção não tratada
      setState((prevState) => ({
        ...prevState,
        response: null,
        isError: true,
        error: err
      }));
    }
  }, [service]);

  useEffect(() => {
    if (!manual) {
      fetch();
    }
  }, [fetch, manual]);

  return {
    ...state,
    fetch,
    allQueueOptions,
    allAgentOptions,
    queuesMap,
    agentsMap
  };
};

export default useAgentsLoggedInQueues;
